import React, { useEffect } from 'react';
import { Button, Card, Descriptions, PageHeader } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import { JobResponse, JobStatusType } from '@openapi';

import { Navigation } from '@constants/Navigation';
import api from '@services/api';

import styles from './style.module.scss';
import { dateToString, downloadFile, getDiff } from '@services/utils';
import { DownloadOutlined } from '@ant-design/icons';

const InfoJobPage: React.FC = () => {
  const [job, setJob] = React.useState<JobResponse>();

  let estimatedTime = '';
  if (job?.startedAt && job?.status === JobStatusType.Running) {
    estimatedTime = dateToString(
      new Date(
        ((new Date().getTime() - job?.startedAt.getTime()) * 100) /
          ((job?.processedItemCount || 0) / (job?.totalItemCount || 1)),
      ),
    );
  }

  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    const data = async () => {
      if (id) {
        const newJob = await api.jobs.getJob(id);
        setJob(newJob);
      }
    };
    data();
  }, [id]);

  const onClose = () => {
    history.push(Navigation.Jobs);
  };

  const inputHandle = async () => {
    if (job?.id) {
      const response = await api.jobs.getJobInput(job?.id);
      if (response) {
        const filename = `${job?.type.toLowerCase()}_input_(${job.id}).csv.gz` || 'input';
        if (filename) {
          downloadFile(response, filename);
        }
      }
    }
  };

  const outputHandle = async () => {
    if (job?.id) {
      const response = await api.jobs.getJobReport(job?.id);
      if (response) {
        const filename = `${job?.type.toLowerCase()}_output_(${job.id}).csv.gz` || 'output';
        if (filename) {
          downloadFile(response, filename);
        }
      }
    }
  };

  const rejectionHandle = async () => {
    if (job?.id) {
      const response = await api.jobs.getJobRejection(job?.id);
      if (response) {
        const filename = `${job?.type.toLowerCase()}_log_(${job.id}).csv.gz` || 'rejection';
        if (filename) {
          downloadFile(response, filename);
        }
      }
    }
  };

  return (
    <>
      <PageHeader
        className="site-page-header"
        onBack={onClose}
        style={{ backgroundColor: '#fff', marginBottom: '1rem' }}
        title={`${job?.type} - ${job?.createdAt?.toLocaleString()}`}
      />
      <Card title="Main information" style={{ marginBottom: '1rem' }}>
        <Descriptions
          labelStyle={{ fontWeight: '500', fontSize: '16px' }}
          layout="horizontal"
          column={1}
          bordered
          className={styles.descriptions}
        >
          <Descriptions.Item label="Id">{job?.id}</Descriptions.Item>
          <Descriptions.Item label="Username">{job?.username}</Descriptions.Item>
          <Descriptions.Item label="Type">{job?.type}</Descriptions.Item>
          <Descriptions.Item label="Status">{job?.status}</Descriptions.Item>
          <Descriptions.Item label="Job info">{job?.info}</Descriptions.Item>
          <Descriptions.Item label="Progression">
            {((job?.processedItemCount || 0) / (job?.totalItemCount || 1)) * 100}
          </Descriptions.Item>
          <Descriptions.Item label="Error message">{job?.errorMessage}</Descriptions.Item>
        </Descriptions>
      </Card>
      <Card title="Time" style={{ marginBottom: '1rem' }}>
        <Descriptions
          labelStyle={{ fontWeight: '500', fontSize: '16px' }}
          layout="horizontal"
          column={1}
          bordered
          className={styles.descriptions}
        >
          <Descriptions.Item label="Created at">{job?.createdAt?.toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="Started at">{job?.startedAt?.toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="Finished at">{job?.finishedAt?.toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="Processing time">
            {job?.startedAt && getDiff(job.startedAt, job?.finishedAt || new Date())}
          </Descriptions.Item>
          <Descriptions.Item label="Time left">{estimatedTime}</Descriptions.Item>
        </Descriptions>
      </Card>
      <Card title="Files">
        <Descriptions
          labelStyle={{ fontWeight: '500', fontSize: '16px' }}
          layout="horizontal"
          column={1}
          bordered
          className={styles.descriptions}
        >
          <Descriptions.Item label="Input">
            {job?.inputFileStatus === 'EXISTS' ? (
              <Button disabled={job?.status !== 'FINISHED'} type="primary" onClick={inputHandle}>
                <DownloadOutlined />
              </Button>
            ) : (
              'Not exists'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Output">
            {job?.reportFileStatus === 'EXISTS' ? (
              <Button disabled={job?.status !== 'FINISHED'} type="primary" onClick={outputHandle}>
                <DownloadOutlined />
              </Button>
            ) : (
              'Not exists'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Log">
            {job?.rejectionFileStatus === 'EXISTS' ? (
              <Button disabled={job?.status !== 'FINISHED'} type="primary" onClick={rejectionHandle}>
                <DownloadOutlined />
              </Button>
            ) : (
              'Not exists'
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </>
  );
};

export default React.memo(InfoJobPage);
